import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { ConfirmationDialog } from './ui/confirmation-dialog';
import { Loader2, Plus, Pencil, Trash2, FileText, ClipboardList, CornerDownRight, AlertTriangle } from 'lucide-react';

const APPLIES_TO = [
  { value: 'equipment', label: 'Equipment / Asset lines' },
  { value: 'worker', label: 'Worker / Operator lines' },
  { value: 'attachment', label: 'Attachment lines' },
  { value: 'material', label: 'Material lines' },
  { value: 'fuel_levy', label: 'Fuel Levy lines' },
  { value: 'any', label: 'Any line type' },
  { value: 'description', label: 'Description (non-chargeable header line)' },
];

const CONDITIONS = [
  { value: 'overtime', label: 'When work runs into overtime' },
  { value: 'hours_over', label: 'When hours exceed…' },
  { value: 'always', label: 'Always add this line' },
];

const QTY_SOURCES = [
  { value: 'overtime_hours', label: 'Overtime hours' },
  { value: 'line_hours', label: 'This line’s hours' },
  { value: 'fixed', label: 'Fixed quantity' },
];

const emptyAddLine = () => ({
  condition_type: 'overtime',
  condition_op: 'equals',
  condition_value: '',
  type_name: 'Operator Overtime',
  description_template: 'Operator Overtime\n{ot_start} - {ot_finish}',
  qty_source: 'overtime_hours',
  qty: 1,
  uom: 'HOUR',
  rate_source: 'flat',
  rate_type: 'overtime',
  unit_price: 0,
  account_item_id: '',
});

const emptyRule = () => ({
  scope: 'global',
  equipment_type_ids: [],
  equipment_type_names: [],
  worker_type_ids: [],
  worker_type_names: [],
  attachment_type_ids: [],
  attachment_type_names: [],
  applies_to: 'equipment',
  description_template: '',
  add_lines: [],
  priority: 0,
  is_active: true,
});

// Client-side mirror of the backend renderer (for add-line previews).
const renderLocal = (template, ctx) => {
  if (!template) return '';
  const out = template.replace(/\{([a-z_][a-z0-9_]*)\}/g, (_, k) => (ctx[k] != null ? String(ctx[k]) : ''));
  return out.split('\n').map(l => l.replace(/[ \t]+/g, ' ').trim()).filter(Boolean).join('\n');
};

export const DocketLineRulesSettings = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [tags, setTags] = useState([]);
  const [addLineTags, setAddLineTags] = useState([]);
  const [rateTypes, setRateTypes] = useState([]);
  const [sampleCtx, setSampleCtx] = useState({});
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyRule());
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sampleDockets, setSampleDockets] = useState([]);
  const [previewDocketId, setPreviewDocketId] = useState('');
  const [docketPreview, setDocketPreview] = useState(null);
  const [docketPreviewLoading, setDocketPreviewLoading] = useState(false);
  const templateRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, et, wt, at] = await Promise.all([
        api.get('/docket-line-rules'),
        api.get('/docket-line-rules/tags'),
        api.get('/equipment-types/all').catch(() => ({ data: [] })),
        api.get('/worker-types').catch(() => ({ data: [] })),
        api.get('/attachment-types').catch(() => ({ data: [] })),
      ]);
      setRules(r.data || []);
      setTags(t.data?.tags || []);
      setAddLineTags(t.data?.add_line_tags || []);
      setRateTypes(t.data?.rate_types || []);
      setSampleCtx(t.data?.sample_context || {});
      setEquipmentTypes((et.data || []).filter(e => e.is_active !== false));
      setWorkerTypes((wt.data || []).filter(w => w.is_active !== false && !w.archived));
      setAttachmentTypes((at.data || []).filter(a => a.is_active !== false && !a.archived));
      api.get('/docket-line-rules/sample-dockets').then(res => setSampleDockets(res.data || [])).catch(() => {});
    } catch (e) {
      toast.error('Failed to load docket rules');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dialogOpen) return;
    const handle = setTimeout(async () => {
      try {
        const res = await api.post('/docket-line-rules/preview', { description_template: form.description_template });
        setPreview(res.data?.rendered || '');
      } catch { setPreview(''); }
    }, 300);
    return () => clearTimeout(handle);
  }, [form.description_template, dialogOpen, api]);

  const openAdd = () => { setEditing(null); setForm(emptyRule()); setPreview(''); setPreviewDocketId(''); setDocketPreview(null); setDialogOpen(true); };
  const openEdit = (rule) => {
    const ids = rule.equipment_type_ids?.length ? rule.equipment_type_ids : (rule.equipment_type_id ? [rule.equipment_type_id] : []);
    const names = rule.equipment_type_names?.length ? rule.equipment_type_names : (rule.equipment_type_name ? [rule.equipment_type_name] : []);
    setEditing(rule);
    setForm({
      ...emptyRule(), ...rule,
      equipment_type_ids: ids, equipment_type_names: names,
      worker_type_ids: rule.worker_type_ids || [],
      worker_type_names: rule.worker_type_names || [],
      attachment_type_ids: rule.attachment_type_ids || [],
      attachment_type_names: rule.attachment_type_names || [],
      add_lines: rule.add_lines || [],
    });
    setPreviewDocketId(''); setDocketPreview(null); setDialogOpen(true);
  };

  const addEqType = (id) => {
    if (!id || form.equipment_type_ids.includes(id)) return;
    const et = equipmentTypes.find(e => e.id === id);
    setForm(f => ({ ...f, equipment_type_ids: [...f.equipment_type_ids, id], equipment_type_names: [...f.equipment_type_names, et?.name || id] }));
  };
  const removeEqType = (id) => setForm(f => {
    const i = f.equipment_type_ids.indexOf(id);
    if (i < 0) return f;
    return { ...f, equipment_type_ids: f.equipment_type_ids.filter(x => x !== id), equipment_type_names: f.equipment_type_names.filter((_, idx) => idx !== i) };
  });

  const addWorkerType = (id) => {
    if (!id || form.worker_type_ids.includes(id)) return;
    const wt = workerTypes.find(w => w.id === id);
    setForm(f => ({ ...f, worker_type_ids: [...f.worker_type_ids, id], worker_type_names: [...f.worker_type_names, wt?.name || id] }));
  };
  const removeWorkerType = (id) => setForm(f => {
    const i = f.worker_type_ids.indexOf(id);
    if (i < 0) return f;
    return { ...f, worker_type_ids: f.worker_type_ids.filter(x => x !== id), worker_type_names: f.worker_type_names.filter((_, idx) => idx !== i) };
  });

  const addAttachmentType = (id) => {
    if (!id || form.attachment_type_ids.includes(id)) return;
    const at = attachmentTypes.find(a => a.id === id);
    setForm(f => ({ ...f, attachment_type_ids: [...f.attachment_type_ids, id], attachment_type_names: [...f.attachment_type_names, at?.name || id] }));
  };
  const removeAttachmentType = (id) => setForm(f => {
    const i = f.attachment_type_ids.indexOf(id);
    if (i < 0) return f;
    return { ...f, attachment_type_ids: f.attachment_type_ids.filter(x => x !== id), attachment_type_names: f.attachment_type_names.filter((_, idx) => idx !== i) };
  });

  // Debounced "preview on a real docket" — runs the DRAFT rule through the real engine.
  useEffect(() => {
    if (!dialogOpen || !previewDocketId) { setDocketPreview(null); return; }
    setDocketPreviewLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await api.post('/docket-line-rules/preview-docket', { docket_id: previewDocketId, rule: buildRulePayload() });
        setDocketPreview(res.data?.lines || []);
      } catch { setDocketPreview([]); }
      finally { setDocketPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewDocketId, dialogOpen, form]);

  const insertTag = (tag) => {
    const el = templateRef.current;
    const token = `{${tag}}`;
    if (!el) { setForm(f => ({ ...f, description_template: (f.description_template || '') + token })); return; }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = form.description_template.slice(0, start) + token + form.description_template.slice(end);
    setForm(f => ({ ...f, description_template: next }));
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = start + token.length; });
  };

  // ---- add-line helpers ----
  const addAddLine = () => setForm(f => ({ ...f, add_lines: [...f.add_lines, emptyAddLine()] }));
  const updateAddLine = (idx, patch) => setForm(f => ({ ...f, add_lines: f.add_lines.map((a, i) => i === idx ? { ...a, ...patch } : a) }));
  const removeAddLine = (idx) => setForm(f => ({ ...f, add_lines: f.add_lines.filter((_, i) => i !== idx) }));

  const buildRulePayload = () => ({
    scope: form.scope,
    equipment_type_ids: form.scope === 'equipment_type' ? form.equipment_type_ids : [],
    equipment_type_names: form.scope === 'equipment_type' ? form.equipment_type_names : [],
    worker_type_ids: form.scope === 'worker_type' ? form.worker_type_ids : [],
    worker_type_names: form.scope === 'worker_type' ? form.worker_type_names : [],
    attachment_type_ids: form.scope === 'attachment_type' ? form.attachment_type_ids : [],
    attachment_type_names: form.scope === 'attachment_type' ? form.attachment_type_names : [],
    applies_to: form.applies_to,
    description_template: form.description_template,
    add_lines: form.add_lines.map(a => ({
      ...a,
      qty: parseFloat(a.qty) || 0,
      unit_price: parseFloat(a.unit_price) || 0,
    })),
    priority: form.priority || 0,
    is_active: form.is_active,
  });

  const handleSave = async () => {
    if (!form.description_template.trim() && form.add_lines.length === 0) { toast.error('Enter a description template or at least one add-line'); return; }
    if (form.scope === 'equipment_type' && form.equipment_type_ids.length === 0) { toast.error('Select at least one asset type'); return; }
    if (form.scope === 'worker_type' && form.worker_type_ids.length === 0) { toast.error('Select at least one worker type'); return; }
    if (form.scope === 'attachment_type' && form.attachment_type_ids.length === 0) { toast.error('Select at least one attachment type'); return; }
    setSaving(true);
    try {
      const payload = buildRulePayload();
      if (editing?.id) await api.put(`/docket-line-rules/${editing.id}`, payload);
      else await api.post('/docket-line-rules', payload);
      toast.success(editing?.id ? 'Rule updated' : 'Rule created');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await api.put(`/docket-line-rules/${rule.id}`, { is_active: !rule.is_active });
      setRules(rs => rs.map(r => r.id === rule.id ? { ...r, is_active: !r.is_active } : r));
    } catch { toast.error('Failed to update rule'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/docket-line-rules/${deleteTarget.id}`);
      toast.success('Rule deleted');
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete rule'); }
  };

  const scopeLabel = (rule) => {
    if (rule.scope === 'equipment_type') {
      const names = rule.equipment_type_names?.length ? rule.equipment_type_names : (rule.equipment_type_name ? [rule.equipment_type_name] : []);
      return names.length ? names.join(', ') : 'Asset type';
    }
    if (rule.scope === 'worker_type') {
      const names = rule.worker_type_names || [];
      return names.length ? names.join(', ') : 'Worker type';
    }
    if (rule.scope === 'attachment_type') {
      const names = rule.attachment_type_names || [];
      return names.length ? names.join(', ') : 'Attachment type';
    }
    return 'Global (all)';
  };

  return (
    <Card className="card-industrial" data-testid="docket-rules-section">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Docket Line Descriptions
            </CardTitle>
            <CardDescription>
              Control how docket line descriptions are composed, and inject extra lines (e.g. Operator Overtime) using job &amp; docket data. Global or per-asset-type. These flow onto the docket, PDF and accounting invoice.
            </CardDescription>
          </div>
          <Button onClick={openAdd} className="btn-volt" data-testid="docket-rule-add-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Rule
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading rules…</div>
        ) : rules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-60" />
            <p className="text-sm">No rules yet. Add one to customise docket line descriptions.</p>
            <p className="text-xs mt-1">Without a rule, descriptions use the default asset-type wording.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map(rule => (
              <div key={rule.id} className={`rounded-lg border p-3 flex items-start justify-between gap-3 ${rule.is_active ? '' : 'opacity-60'}`} data-testid={`docket-rule-row-${rule.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={rule.scope === 'equipment_type' ? 'default' : 'outline'}>{scopeLabel(rule)}</Badge>
                    <Badge variant="outline">{APPLIES_TO.find(a => a.value === rule.applies_to)?.label || rule.applies_to}</Badge>
                    {rule.add_lines?.length > 0 && <Badge variant="secondary" data-testid={`docket-rule-addlines-count-${rule.id}`}>+{rule.add_lines.length} add-line{rule.add_lines.length > 1 ? 's' : ''}</Badge>}
                    {!rule.is_active && <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>}
                  </div>
                  {rule.description_template && <pre className="mt-2 text-xs whitespace-pre-wrap font-mono text-muted-foreground break-words">{rule.description_template}</pre>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Switch checked={rule.is_active} onCheckedChange={() => toggleActive(rule)} data-testid={`docket-rule-active-${rule.id}`} />
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(rule)} data-testid={`docket-rule-edit-${rule.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => setDeleteTarget(rule)} data-testid={`docket-rule-delete-${rule.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="docket-rule-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit' : 'Add'} Docket Line Rule</DialogTitle>
            <DialogDescription>Compose the line description and optionally inject conditional extra lines.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Scope</Label>
                <Select value={form.scope} onValueChange={(v) => setForm(f => ({ ...f, scope: v, equipment_type_ids: [], equipment_type_names: [], worker_type_ids: [], worker_type_names: [], attachment_type_ids: [], attachment_type_names: [] }))}>
                  <SelectTrigger data-testid="docket-rule-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (all)</SelectItem>
                    <SelectItem value="equipment_type">Specific asset type(s)</SelectItem>
                    <SelectItem value="worker_type">Specific worker type(s)</SelectItem>
                    <SelectItem value="attachment_type">Specific attachment type(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.scope === 'equipment_type' && (
              <div>
                <Label>Asset type(s)</Label>
                <div className="flex flex-wrap gap-1 mb-2" data-testid="docket-rule-eqtype-chips">
                  {form.equipment_type_ids.length === 0
                    ? <span className="text-xs text-muted-foreground">None selected yet</span>
                    : form.equipment_type_ids.map((id, i) => (
                      <Badge key={id} variant="secondary" className="gap-1" data-testid={`docket-rule-eqtype-chip-${id}`}>
                        {form.equipment_type_names[i] || id}
                        <button type="button" className="ml-0.5 hover:text-destructive" onClick={() => removeEqType(id)} data-testid={`docket-rule-eqtype-remove-${id}`}>×</button>
                      </Badge>
                    ))}
                </div>
                <Select value="" onValueChange={addEqType}>
                  <SelectTrigger data-testid="docket-rule-eqtype-select"><SelectValue placeholder="Add asset type…" /></SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.filter(et => !form.equipment_type_ids.includes(et.id)).map(et => <SelectItem key={et.id} value={et.id}>{et.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === 'worker_type' && (
              <div>
                <Label>Worker type(s)</Label>
                <div className="flex flex-wrap gap-1 mb-2" data-testid="docket-rule-workertype-chips">
                  {form.worker_type_ids.length === 0
                    ? <span className="text-xs text-muted-foreground">None selected yet</span>
                    : form.worker_type_ids.map((id, i) => (
                      <Badge key={id} variant="secondary" className="gap-1" data-testid={`docket-rule-workertype-chip-${id}`}>
                        {form.worker_type_names[i] || id}
                        <button type="button" className="ml-0.5 hover:text-destructive" onClick={() => removeWorkerType(id)}>×</button>
                      </Badge>
                    ))}
                </div>
                <Select value="" onValueChange={addWorkerType}>
                  <SelectTrigger data-testid="docket-rule-workertype-select"><SelectValue placeholder="Add worker type…" /></SelectTrigger>
                  <SelectContent>
                    {workerTypes.filter(wt => !form.worker_type_ids.includes(wt.id)).map(wt => <SelectItem key={wt.id} value={wt.id}>{wt.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === 'attachment_type' && (
              <div>
                <Label>Attachment type(s)</Label>
                <div className="flex flex-wrap gap-1 mb-2" data-testid="docket-rule-attachmenttype-chips">
                  {form.attachment_type_ids.length === 0
                    ? <span className="text-xs text-muted-foreground">None selected yet</span>
                    : form.attachment_type_ids.map((id, i) => (
                      <Badge key={id} variant="secondary" className="gap-1" data-testid={`docket-rule-attachmenttype-chip-${id}`}>
                        {form.attachment_type_names[i] || id}
                        <button type="button" className="ml-0.5 hover:text-destructive" onClick={() => removeAttachmentType(id)}>×</button>
                      </Badge>
                    ))}
                </div>
                <Select value="" onValueChange={addAttachmentType}>
                  <SelectTrigger data-testid="docket-rule-attachmenttype-select"><SelectValue placeholder="Add attachment type…" /></SelectTrigger>
                  <SelectContent>
                    {attachmentTypes.filter(at => !form.attachment_type_ids.includes(at.id)).map(at => <SelectItem key={at.id} value={at.id}>{at.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Line type</Label>
              <Select value={form.applies_to} onValueChange={(v) => setForm(f => ({ ...f, applies_to: v }))}>
                <SelectTrigger data-testid="docket-rule-appliesto-select"><SelectValue /></SelectTrigger>
                <SelectContent>{APPLIES_TO.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div>
              <Label>Description template</Label>
              <Textarea ref={templateRef} rows={3} value={form.description_template}
                onChange={(e) => setForm(f => ({ ...f, description_template: e.target.value }))}
                placeholder={'e.g. {asset_type} c/w operator at {site}\n{date} {start} - {finish}'}
                className="font-mono text-sm" data-testid="docket-rule-template-input" />
              <div className="flex flex-wrap gap-1 mt-2">
                {tags.map(tag => (
                  <button key={tag} type="button" onClick={() => insertTag(tag)}
                    className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-muted transition-colors"
                    data-testid={`docket-rule-tag-${tag}`}>{`{${tag}}`}</button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Live preview (sample data)</Label>
              <pre className="mt-1 rounded-md border bg-muted/40 p-2 text-xs whitespace-pre-wrap min-h-[3rem]" data-testid="docket-rule-preview">{preview || '—'}</pre>
            </div>

            {/* ---- Conditional add-lines ---- */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2"><CornerDownRight className="h-4 w-4" /> Conditional add-lines</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAddLine} data-testid="docket-rule-add-line-btn">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add line
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">Inject an extra line (e.g. Operator Overtime) when a condition is met.</p>
              {form.add_lines.length > 0 && (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-400 bg-amber-50 dark:bg-amber-950/30 p-2 text-amber-800 dark:text-amber-300 text-[11px]">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>If your tenant Overtime rule (above) is also on, an overtime add-line here will produce a second OT line. Use one or the other.</span>
                </div>
              )}

              <div className="space-y-3 mt-3">
                {form.add_lines.map((al, idx) => (
                  <div key={idx} className="rounded-lg border p-3 space-y-3 bg-muted/20" data-testid={`docket-rule-addline-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold">Add-line #{idx + 1}</span>
                      <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => removeAddLine(idx)} data-testid={`docket-rule-addline-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Condition</Label>
                        <Select value={al.condition_type} onValueChange={(v) => updateAddLine(idx, { condition_type: v })}>
                          <SelectTrigger data-testid={`docket-rule-addline-cond-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{CONDITIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {al.condition_type === 'hours_over' && (
                        <div>
                          <Label className="text-xs">Hours over</Label>
                          <Input type="number" step="0.5" value={al.condition_value || ''} onChange={(e) => updateAddLine(idx, { condition_value: e.target.value })} data-testid={`docket-rule-addline-hours-${idx}`} />
                        </div>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs">Line label</Label>
                      <Input value={al.type_name || ''} onChange={(e) => updateAddLine(idx, { type_name: e.target.value })} data-testid={`docket-rule-addline-label-${idx}`} />
                    </div>

                    <div>
                      <Label className="text-xs">Description template</Label>
                      <Textarea rows={2} value={al.description_template} onChange={(e) => updateAddLine(idx, { description_template: e.target.value })} className="font-mono text-sm" data-testid={`docket-rule-addline-desc-${idx}`} />
                      <div className="flex flex-wrap gap-1 mt-1">
                        {[...addLineTags, 'operator', 'asset_type', 'date'].map(tag => (
                          <button key={tag} type="button"
                            onClick={() => updateAddLine(idx, { description_template: (al.description_template || '') + `{${tag}}` })}
                            className="text-[11px] px-2 py-0.5 rounded-full border hover:bg-muted transition-colors"
                            data-testid={`docket-rule-addline-tag-${idx}-${tag}`}>{`{${tag}}`}</button>
                        ))}
                      </div>
                      <pre className="mt-1 rounded-md border bg-muted/40 p-2 text-[11px] whitespace-pre-wrap min-h-[2rem]" data-testid={`docket-rule-addline-preview-${idx}`}>{renderLocal(al.description_template, sampleCtx) || '—'}</pre>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Quantity from</Label>
                        <Select value={al.qty_source} onValueChange={(v) => updateAddLine(idx, { qty_source: v })}>
                          <SelectTrigger data-testid={`docket-rule-addline-qtysrc-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{QTY_SOURCES.map(q => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {al.qty_source === 'fixed' && (
                        <div>
                          <Label className="text-xs">Qty</Label>
                          <Input type="number" step="0.5" value={al.qty} onChange={(e) => updateAddLine(idx, { qty: e.target.value })} data-testid={`docket-rule-addline-qty-${idx}`} />
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Rate from</Label>
                        <Select value={al.rate_source || 'flat'} onValueChange={(v) => updateAddLine(idx, { rate_source: v })}>
                          <SelectTrigger data-testid={`docket-rule-addline-ratesrc-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flat">Fixed amount</SelectItem>
                            <SelectItem value="price_list">Customer price list</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {al.rate_source === 'price_list' ? (
                        <div>
                          <Label className="text-xs">Price-list rate type</Label>
                          <Select value={al.rate_type || 'overtime'} onValueChange={(v) => updateAddLine(idx, { rate_type: v })}>
                            <SelectTrigger data-testid={`docket-rule-addline-ratetype-${idx}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{rateTypes.map(rt => <SelectItem key={rt} value={rt}>{rt}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs">Rate (unit price)</Label>
                          <Input type="number" step="0.01" value={al.unit_price} onChange={(e) => updateAddLine(idx, { unit_price: e.target.value })} data-testid={`docket-rule-addline-rate-${idx}`} />
                        </div>
                      )}
                    </div>
                    {al.rate_source === 'price_list' && (
                      <p className="text-[11px] text-muted-foreground">Pulls the <strong>{al.rate_type || 'overtime'}</strong> rate from the customer's price list for this asset type. If that rate isn't configured, the line is added at 0 with a warning.</p>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">UOM</Label>
                        <Input value={al.uom || ''} onChange={(e) => updateAddLine(idx, { uom: e.target.value })} data-testid={`docket-rule-addline-uom-${idx}`} />
                      </div>
                      <div>
                        <Label className="text-xs">Account ID (accounting export)</Label>
                        <Input value={al.account_item_id || ''} onChange={(e) => updateAddLine(idx, { account_item_id: e.target.value })} placeholder="optional" data-testid={`docket-rule-addline-acct-${idx}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* ---- Preview on a real docket ---- */}
            <div className="border-t pt-4 space-y-2">
              <Label className="flex items-center gap-2"><FileText className="h-4 w-4" /> Preview on a real docket</Label>
              <p className="text-[11px] text-muted-foreground">See exactly how this rule renders (base line + injected add-lines) on an existing docket — nothing is saved.</p>
              <Select value={previewDocketId} onValueChange={setPreviewDocketId}>
                <SelectTrigger data-testid="docket-rule-preview-docket-select"><SelectValue placeholder="Choose a docket…" /></SelectTrigger>
                <SelectContent>
                  {sampleDockets.map(d => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {previewDocketId && (
                <div className="rounded-md border max-h-56 overflow-y-auto divide-y" data-testid="docket-rule-docket-preview">
                  {docketPreviewLoading ? (
                    <div className="flex items-center gap-2 text-muted-foreground text-xs p-3"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Rendering…</div>
                  ) : (docketPreview && docketPreview.length > 0) ? (
                    docketPreview.map((l, i) => (
                      <div key={i} className={`p-2 text-xs ${l.rule_generated ? 'bg-emerald-50 dark:bg-emerald-950/20' : ''}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            {l.rule_generated && <Badge variant="secondary" className="mr-1 text-[10px] align-middle">added</Badge>}
                            <span className="whitespace-pre-wrap break-words align-middle">{l.description || l.display_name || l.item_type}</span>
                            {l.rule_warning && <span className="block text-amber-600 dark:text-amber-400 mt-0.5">⚠ {l.rule_warning}</span>}
                          </div>
                          <span className="font-mono shrink-0 tabular-nums">{l.qty} {l.uom} × {Number(l.unit_price || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-muted-foreground text-xs p-3">No lines to show for this docket.</div>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between border-t pt-4">
              <Label>Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))} data-testid="docket-rule-active-toggle" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving} data-testid="docket-rule-cancel-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-volt" data-testid="docket-rule-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save Rule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Rule"
        description="Delete this docket line rule? Existing dockets keep their current descriptions until regenerated."
        confirmText="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        testId="docket-rule-delete-dialog"
      />
    </Card>
  );
};

export default DocketLineRulesSettings;
